import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function CashFlowChart({ totalIn, totalOut, netBalance, loading }) {
  // Bar chart configuration
  const barData = {
    labels: ['Cash In', 'Cash Out'],
    datasets: [
      {
        label: 'Amount (₹)',
        data: [totalIn, totalOut],
        backgroundColor: [
          'rgba(22, 163, 74, 0.8)',  // green for Cash In
          'rgba(220, 38, 38, 0.8)',  // red for Cash Out
        ],
        borderColor: [
          'rgb(22, 163, 74)',
          'rgb(220, 38, 38)',
        ],
        borderWidth: 2,
        borderRadius: 8,
        barThickness: 60,
      },
    ],
  }

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return '₹' + context.parsed.y.toLocaleString('en-IN')
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + (value / 1000).toFixed(0) + 'k'
          },
          color: '#9ca3af',
          font: {
            size: 11,
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
      },
      x: {
        ticks: {
          color: '#4b5563',
          font: {
            size: 12,
            weight: '500',
          },
        },
        grid: {
          display: false,
        },
      },
    },
  }

  // Doughnut chart configuration
  const doughnutData = {
    labels: ['Cash In', 'Cash Out'],
    datasets: [
      {
        data: [totalIn, totalOut],
        backgroundColor: [
          'rgba(22, 163, 74, 0.8)',
          'rgba(220, 38, 38, 0.8)',
        ],
        borderColor: [
          'rgb(22, 163, 74)',
          'rgb(220, 38, 38)',
        ],
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((context.parsed / total) * 100).toFixed(1)
            return context.label + ': ₹' + context.parsed.toLocaleString('en-IN') + ' (' + percentage + '%)'
          }
        }
      },
    },
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 border border-gray-100">
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-4">Loading chart...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 border border-gray-100">
      <div className="mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800">Cash Flow Overview</h3>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">Cash In vs Cash Out comparison</p>
      </div>

      {/* Bar Chart */}
      <div className="mb-6" style={{ height: '200px' }}>
        <Bar data={barData} options={barOptions} />
      </div>

      {/* Doughnut Chart and Summary */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="w-full sm:w-1/2 relative" style={{ height: '180px' }}>
          <Doughnut data={doughnutData} options={doughnutOptions} />
        </div>

        {/* Summary Stats */}
        <div className="w-full sm:w-1/2 space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-600"></div>
              <span className="text-sm font-medium text-gray-700">Cash In</span>
            </div>
            <span className="text-sm font-bold text-green-700">₹{totalIn.toLocaleString('en-IN')}</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-600"></div>
              <span className="text-sm font-medium text-gray-700">Cash Out</span>
            </div>
            <span className="text-sm font-bold text-red-700">₹{totalOut.toLocaleString('en-IN')}</span>
          </div>

          <div className={`flex items-center justify-between p-3 rounded-lg border ${netBalance >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-orange-50 border-orange-100'}`}>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${netBalance >= 0 ? 'bg-blue-600' : 'bg-orange-600'}`}></div>
              <span className="text-sm font-medium text-gray-700">Net Balance</span>
            </div>
            <span className={`text-sm font-bold ${netBalance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
              ₹{netBalance.toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CashFlowChart
